/**
 * Report View
 *
 * Renders the standard summary report as a presentation page (HTML + inline
 * SVG charts) from the facts produced by computeSummaryReport. This is the
 * "print to PDF" surface; the Docs page (reportDoc.js) is the editable one.
 * Both read the same facts, so their numbers always match.
 *
 * Pure: returns an HTML string. Text comes from the injected t(); every value
 * interpolated into markup is escaped. Charts are drawn with one scale per
 * chart and colored through the .report-view CSS tokens (style.css).
 *
 * @module services/reportView
 */

import { buildHighlightItems, makeFormatters, makeReportLinks } from './reportDoc.js';
import { REPORT_PRIORITIES, REPORT_STATUSES, hasStartedWork } from './reportMetrics.js';

const STATUS_ORDER = REPORT_STATUSES;
const STATE_CLASS = {
    late: 'late', behind: 'behind', onTrack: 'ontrack', ahead: 'ahead',
    notStarted: 'muted', noDates: 'muted', noTasks: 'muted', done: 'ahead'
};
// Caps keep the report readable with 100+ projects; rows are already sorted
// by what needs attention first, and an info note says what was left out.
const MAX_TIMELINE_ROWS = 20;
const MAX_TIMELINE_MONTHS = 18;
const MAX_UPCOMING = 10; // same as computeSummaryReport upcomingLimit, so view and Docs page match

export function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
const esc = escapeHtml;

function dayMs(iso) {
    return Date.parse(`${iso}T00:00:00Z`);
}

function truncate(value, max) {
    const s = String(value || '');
    return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

// Rough text width for label collision checks (system sans, 11–12px).
function textWidth(s, size = 12) {
    return String(s).length * size * 0.56;
}

function niceCeil(v) {
    if (v <= 4) return 4;
    const pow = Math.pow(10, Math.floor(Math.log10(v)));
    for (const m of [1, 2, 2.5, 4, 5, 10]) {
        if (m * pow >= v) return m * pow;
    }
    return 10 * pow;
}

// Months shown in the work-pace chart and the header period: leading months
// with no activity are dropped (at least two are kept).
function activeMonths(r) {
    const months = r.throughput.months;
    const first = months.findIndex(mm => mm.created > 0 || mm.completed > 0);
    const start = first < 0 ? 0 : Math.min(first, Math.max(0, months.length - 2));
    return months.slice(start);
}

const INFO_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>';

const OPEN_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 17 17 7"/><path d="M8 7h9v9"/></svg>';

function linkHtml(href, label) {
    return href ? `<a class="rv-link" href="${esc(href)}" target="_blank" rel="noopener"><span>${esc(label)}</span>${OPEN_ICON}</a>` : '';
}

// A project/task name that opens it in Nautilus in a new tab (plain text in print).
function nameLink(href, label) {
    return href ? `<a class="rv-name-link" href="${esc(href)}" target="_blank" rel="noopener">${esc(label)}</a>` : esc(label);
}

// Flat info note. Never lists project names: counts only, so it stays short at any scale.
function infoNote(lead, rest = '', extraClass = '', link = '') {
    return `<div class="rv-info${extraClass ? ` ${extraClass}` : ''}" role="note">${INFO_ICON}<div class="rv-info-body"><p>${lead ? `<b>${esc(lead)}</b>` : ''}${lead && rest ? ' ' : ''}${esc(rest)}</p>${link}</div></div>`;
}

function section(type, inner) {
    return `<section class="rv-section" data-section="${type}">${inner}</section>`;
}

function secHead(title, sub, aside = '') {
    return `<div class="rv-sec-head"><div><h2>${esc(title)}</h2>${sub ? `<p class="rv-sub">${esc(sub)}</p>` : ''}</div>${aside}</div>`;
}

function legend(items) {
    return `<div class="rv-legend">${items.map(([cls, label]) => `<span><i class="rv-fill-${cls}"></i>${esc(label)}</span>`).join('')}</div>`;
}

// ── Header + KPIs ───────────────────────────────────────────────────────
function headerSection(r, t, f, title, timeLabel) {
    const firstMonth = activeMonths(r)[0]?.month;
    return section('header', `
        <div class="rv-eyebrow">${esc(t('report.view.eyebrow'))}</div>
        <h1 class="rv-title" contenteditable="true" spellcheck="false" data-rv-title title="${esc(t('report.view.titleHint'))}">${esc(title)}</h1>
        <div class="rv-meta">
            <span>${esc(t('report.view.meta.asOf'))} <b>${esc(timeLabel ? t('report.view.meta.dateTime', { date: f.day(r.cutDate), time: timeLabel }) : f.day(r.cutDate))}</b></span>
            ${firstMonth ? `<span>${esc(t('report.view.meta.period'))} <b>${esc(f.month(firstMonth))} – ${esc(f.month(r.cutDate.slice(0, 7)))}</b></span>` : ''}
            <span>${esc(t('report.view.meta.counts', { projects: r.kpis.projects, tasks: r.kpis.totalTasks }))}</span>
        </div>`);
}

function kpiSection(r, t, f) {
    const k = r.kpis;
    const tile = (value, label, note, alert = false) =>
        `<div class="rv-kpi${alert ? ' alert' : ''}"><div class="v">${value}</div><div class="l">${esc(label)}</div><div class="n">${note}</div></div>`;
    const m = k.nextMilestone;
    return section('kpis', `<div class="rv-kpis">
        ${tile(esc(k.totalTasks), t('report.summary.kpis.tasks'), esc(t('report.view.kpi.createdMonth', { count: k.createdThisMonth })))}
        ${tile(`${esc(k.counts.done)}<small>${esc(k.donePct)}%</small>`, t('report.summary.kpis.completed'), esc(t('report.view.kpi.completedMonth', { count: k.completedThisMonth })))}
        ${tile(esc(k.counts.progress + k.counts.review), t('report.summary.kpis.inProgress'), esc(t('report.view.kpi.inProjects', { count: k.projectsInProgress })))}
        ${tile(esc(k.overdue), t('report.summary.kpis.overdue'), esc(t('report.summary.kpis.overdueDetail', { pct: k.overduePctOfOpen })), k.overdue > 0)}
        ${tile(esc(k.lateProjects), t('report.summary.kpis.lateProjects'), esc(t('report.view.kpi.lateProjectsNote')), k.lateProjects > 0)}
        ${m
            ? tile(`${esc(m.daysLeft)}<small>${esc(t('report.view.kpi.days'))}</small>`, t('report.summary.kpis.nextMilestone'), `${esc(m.name)} · ${esc(f.day(m.endDate))}`)
            : tile(esc(t('report.summary.common.none')), t('report.summary.kpis.nextMilestone'), esc(t('report.summary.kpis.noMilestone')))}
    </div>`);
}

function highlightsSection(r, t, f) {
    const items = buildHighlightItems(r, t, f);
    return section('highlights', `
        ${secHead(t('report.summary.highlights.heading'), '', `<span class="rv-source">${esc(t('report.view.highlights.source'))}</span>`)}
        <ul class="rv-highlights">${items.map(i => `<li class="tone-${i.tone}"><span class="dot" aria-hidden="true"></span><span>${esc(i.text)}</span></li>`).join('')}</ul>`);
}

// ── Status by project ───────────────────────────────────────────────────
// Every project in course (some task past to-do), sorted by what needs attention.
// Projects still in planning/backlog (all tasks to-do or backlog, or no tasks) are
// hidden and counted in the note; archived projects never reach this list.
function portfolioSection(r, t, f, links) {
    const started = r.projects.filter(p => p.started);
    const hidden = r.projects.length - started.length;
    const rows = started.map(p => ({
        id: p.id,
        name: p.name,
        sub: [t(`report.summary.state.${p.state}`), p.endDate ? t('report.view.portfolio.ends', { date: f.day(p.endDate) }) : ''].filter(Boolean).join(' · '),
        state: p.state,
        counts: p.counts,
        taskIdsByStatus: p.taskIdsByStatus,
        total: p.total
    }));
    if (hasStartedWork(r.unassigned.counts)) rows.push({ name: t('report.summary.projects.unassigned'), sub: '', counts: r.unassigned.counts, taskIdsByStatus: r.unassigned.taskIdsByStatus, total: r.unassigned.total });
    const note = hidden > 0
        ? infoNote(t('report.view.portfolio.activeOnly'), t(hidden === 1 ? 'report.view.portfolio.hiddenOne' : 'report.view.portfolio.hidden', { count: hidden }), '',
            linkHtml(links.projects(r.projects.filter(p => !p.started).map(p => p.id)), t('report.view.link.projects')))
        : '';
    if (!rows.length) {
        const empty = r.projects.length ? t('report.view.portfolio.noneActive') : t('report.summary.projects.empty');
        return section('portfolio', `${secHead(t('report.view.portfolio.heading'))}<p class="rv-empty">${esc(empty)}</p>${note}`);
    }
    const present = STATUS_ORDER.filter(s => rows.some(row => row.counts[s] > 0));
    const max = Math.max(1, ...rows.map(row => row.total));
    const body = rows.map(row => {
        const name = `<div class="rv-pf-name" title="${esc(row.name)}">${row.id != null ? nameLink(links.project(row.id), row.name) : esc(row.name)}${row.sub ? `<small class="${row.state ? `st-${STATE_CLASS[row.state]}` : ''}">${esc(row.sub)}</small>` : ''}</div>`;
        if (!row.total) return `${name}<div class="rv-pf-empty">${esc(t('report.summary.state.noTasks'))}</div><div class="rv-pf-tot">0</div>`;
        // Each segment opens exactly its tasks (this project, this status) in a new tab.
        const segs = STATUS_ORDER.filter(s => row.counts[s] > 0).map(s => {
            const href = links.tasks(row.taskIdsByStatus?.[s] || []);
            const tip = `<b>${esc(esc(row.name))}</b><br>${esc(esc(t('report.view.portfolio.tip', { status: t(`tasks.status.${s}`), count: row.counts[s], total: row.total })))}${href ? `<br><span class=&quot;rv-tip-hint&quot;>${esc(esc(t('report.view.portfolio.clickHint')))}</span>` : ''}`;
            const attrs = `class="rv-seg rv-fill-${s}" style="width:${((row.counts[s] / max) * 100).toFixed(2)}%" data-tip="${tip}"`;
            return href
                ? `<a ${attrs} href="${esc(href)}" target="_blank" rel="noopener" aria-label="${esc(`${row.name}: ${t(`tasks.status.${s}`)} ${row.counts[s]}`)}"></a>`
                : `<div ${attrs}></div>`;
        }).join('');
        return `${name}<div class="rv-pf-bar">${segs}</div><div class="rv-pf-tot">${esc(row.total)}</div>`;
    }).join('');
    return section('portfolio', `
        ${secHead(t('report.view.portfolio.heading'), t('report.view.portfolio.sub'), legend(present.map(s => [s, t(`tasks.status.${s}`)])))}
        <div class="rv-pf">${body}</div>
        ${note}`);
}

// ── Progress vs time ────────────────────────────────────────────────────
function paceSection(r, t, f, links) {
    const charted = r.projects.filter(p => p.total > 0 && p.timePct !== null && ['late', 'behind', 'onTrack', 'ahead', 'done'].includes(p.state));
    const outside = {
        notStarted: r.projects.filter(p => p.state === 'notStarted'),
        noDates: r.projects.filter(p => p.state === 'noDates'),
        noTasks: r.projects.filter(p => p.state === 'noTasks')
    };
    const head = secHead(t('report.view.pace.heading'), t('report.view.pace.sub'));
    const outsideParts = Object.entries(outside).filter(([, list]) => list.length)
        .map(([key, list]) => t(`report.view.pace.outsideCount.${key}`, { count: list.length }));
    const outsideTotal = Object.values(outside).reduce((sum, list) => sum + list.length, 0);
    const outsideNote = outsideTotal
        ? infoNote(t('report.view.pace.outsideNote'), t(outsideTotal === 1 ? 'report.view.pace.excludedOne' : 'report.view.pace.excluded', { count: outsideTotal, parts: outsideParts.join(', ') }), 'rv-info--side',
            linkHtml(links.projects(Object.values(outside).flat().map(p => p.id)), t('report.view.link.projects')))
        : '';

    if (!charted.length) {
        return section('pace', `${head}<p class="rv-empty">${esc(t('report.view.pace.empty'))}</p>${outsideNote}`);
    }

    // Merge projects that land on the same point.
    const points = [];
    charted.forEach(p => {
        const tx = p.state === 'late' ? 100 : p.timePct;
        const existing = points.find(pt => pt.t === tx && pt.p === p.donePct);
        if (existing) existing.items.push(p);
        else points.push({ t: tx, p: p.donePct, items: [p] });
    });

    const W = 520, H = 340, m = { l: 48, r: 24, t: 16, b: 40 };
    const x = v => m.l + (v / 100) * (W - m.l - m.r);
    const y = v => H - m.b - (v / 100) * (H - m.t - m.b);
    const angle = Math.atan((H - m.t - m.b) / (W - m.l - m.r)) * 180 / Math.PI;
    let s = `<svg class="rv-chart" viewBox="0 0 ${W} ${H}" role="img" aria-label="${esc(t('report.view.pace.heading'))}">`;
    s += `<polygon class="rv-zone-behind" points="${x(0)},${y(0)} ${x(100)},${y(0)} ${x(100)},${y(100)}"/>`;
    for (let i = 0; i <= 100; i += 25) {
        s += `<line class="rv-grid" x1="${x(0)}" x2="${x(100)}" y1="${y(i)}" y2="${y(i)}"/>`;
        s += `<text x="${x(0) - 8}" y="${y(i) + 4}" text-anchor="end">${i}%</text>`;
        s += `<text x="${x(i)}" y="${H - m.b + 18}" text-anchor="middle">${i}%</text>`;
    }
    s += `<line class="rv-axis" x1="${x(0)}" x2="${x(100)}" y1="${y(0)}" y2="${y(0)}"/>`;
    s += `<line class="rv-diag" x1="${x(0)}" y1="${y(0)}" x2="${x(100)}" y2="${y(100)}"/>`;
    s += `<text x="${x(60) - 6}" y="${y(60) - 8}" text-anchor="end" transform="rotate(-${angle.toFixed(2)} ${x(60) - 6} ${y(60) - 8})">${esc(t('report.view.pace.diagonal'))}</text>`;
    s += `<text class="rv-zone-label" x="${x(97)}" y="${y(42)}" text-anchor="end">${esc(t('report.view.pace.zone'))}</text>`;
    s += `<text x="${x(50)}" y="${H - 4}" text-anchor="middle">${esc(t('report.view.pace.axisX'))}</text>`;
    s += `<text class="rv-axis-title-y" transform="translate(12 ${y(50)}) rotate(-90)" text-anchor="middle">${esc(t('report.view.pace.axisY'))}</text>`;

    // Label placement: try a few vertical offsets until the box clears every
    // label already placed and the zone caption.
    const placed = [{ x1: x(97) - textWidth(t('report.view.pace.zone'), 11), x2: x(97), y1: y(42) - 11, y2: y(42) + 3 }];
    const overlaps = (a, b) => a.x1 < b.x2 && a.x2 > b.x1 && a.y1 < b.y2 && a.y2 > b.y1;
    const labels = [];
    let labelsFit = true;
    points.sort((a, b) => b.p - a.p || a.t - b.t).forEach(pt => {
        const cx = x(pt.t), cy = y(pt.p);
        const label = pt.items.length === 1
            ? pt.items[0].name
            : pt.items.length === 2
                ? t('report.view.pace.joinTwo', { a: pt.items[0].name, b: pt.items[1].name })
                : t('report.view.pace.many', { count: pt.items.length });
        const shown = truncate(label, 48);
        const lateDays = pt.items.find(p => p.state === 'late')?.daysOverdue || 0;
        const sub = lateDays ? t('report.view.pace.lateTag', { days: lateDays }) : '';
        const right = pt.t <= 60;
        const w = Math.max(textWidth(shown), textWidth(sub, 11));
        const lx = right ? cx + 10 : cx - 10;
        let chosen = null;
        for (const dy of [4, -12, 20, -28, 36, -44]) {
            const ly = cy + dy;
            const box = { x1: right ? lx : lx - w, x2: right ? lx + w : lx, y1: ly - 12, y2: ly + (sub ? 16 : 3) };
            if (box.y1 < m.t - 8 || box.y2 > H - m.b + 4) continue;
            if (!placed.some(b => overlaps(b, box))) { chosen = { ly, box }; break; }
        }
        if (!chosen) {
            labelsFit = false;
        } else {
            placed.push(chosen.box);
        }
        const cls = pt.items.some(p => p.state === 'late') ? 'late' : (pt.p >= pt.t ? 'ahead' : 'behind');
        const tip = `<b>${esc(esc(label))}</b><br>${esc(esc(t('report.view.pace.tip', { done: pt.p, time: pt.t })))}${sub ? `<br>${esc(esc(sub))}` : ''}`;
        s += `<g data-tip="${tip}"><circle cx="${cx}" cy="${cy}" r="14" class="rv-hit"/>`;
        s += `<circle cx="${cx}" cy="${cy}" r="6.5" class="rv-dot rv-dot-${cls}"/>`;
        s += `</g>`;
        if (chosen) {
            labels.push(`<text class="rv-lbl" x="${lx}" y="${chosen.ly}" text-anchor="${right ? 'start' : 'end'}">${esc(shown)}</text>`);
            if (sub) labels.push(`<text class="rv-lbl-late" x="${lx}" y="${chosen.ly + 14}" text-anchor="${right ? 'start' : 'end'}">${esc(sub)}</text>`);
        }
    });
    // Never render a partial set of labels: one collision makes the chart harder
    // to read than relying on the complete list directly below it.
    if (labelsFit) s += labels.join('');
    s += `</svg>`;

    // Phones: the in-chart labels shrink to ~7px, so the same points are listed
    // under the chart (hidden on desktop and in print).
    const paceList = `<ul class="rv-pace-list">${points.flatMap(pt => pt.items.map(p => {
        const cls = p.state === 'late' ? 'late' : (pt.p >= pt.t ? 'ahead' : 'behind');
        const sub = p.state === 'late' ? t('report.view.pace.lateTag', { days: p.daysOverdue }) : '';
        return `<li><i class="rv-dot-${cls}" aria-hidden="true"></i><span class="n">${nameLink(links.project(p.id), p.name)}</span><span class="v">${esc(t('report.view.pace.tip', { done: pt.p, time: pt.t }))}${sub ? ` · <b>${esc(sub)}</b>` : ''}</span></li>`;
    })).join('')}</ul>`;

    const notes = [];
    r.projects.filter(p => p.state === 'behind').slice(0, 2).forEach(p => {
        notes.push(`<div class="rv-callout warn">${esc(t('report.summary.highlights.behindOne', { name: p.name, time: p.timePct, done: p.donePct }))}</div>`);
    });
    const best = r.projects.filter(p => p.state === 'ahead').sort((a, b) => (b.donePct - b.timePct) - (a.donePct - a.timePct))[0];
    if (best) notes.push(`<div class="rv-callout">${esc(t('report.view.pace.ahead', { name: best.name, done: best.donePct, time: best.timePct }))}</div>`);
    if (outsideNote) notes.push(outsideNote);

    const paceChartClass = labelsFit ? '' : ' rv-pace-chart--labels-hidden';
    return section('pace', `${head}<div class="rv-two"><div class="rv-pace-chart${paceChartClass}">${s}${paceList}</div><div class="rv-side">${notes.join('')}</div></div>`);
}

// ── Timeline ────────────────────────────────────────────────────────────
function timelineSection(r, t, f, locale, links) {
    const all = r.projects.filter(p => p.startDate && p.endDate).sort((a, b) => a.startDate.localeCompare(b.startDate) || a.name.localeCompare(b.name));
    const head = secHead(t('report.view.timeline.heading'), t('report.view.timeline.sub'));
    // Only projects with both dates can be drawn; say so (and how many are left out).
    const excluded = r.projects.length - all.length;
    const capped = Math.max(0, all.length - MAX_TIMELINE_ROWS);
    const noteParts = [];
    if (excluded > 0) noteParts.push(excluded === 1 ? t('report.view.timeline.excludedOne') : t('report.view.timeline.excluded', { count: excluded }));
    if (capped > 0) noteParts.push(t('report.view.timeline.more', { shown: MAX_TIMELINE_ROWS, total: all.length }));
    const undatedNote = noteParts.length
        ? infoNote(excluded > 0 ? t('report.view.timeline.datedOnly') : '', noteParts.join(' '), '',
            linkHtml(links.projects([...r.projects.filter(p => !(p.startDate && p.endDate)), ...all.slice(MAX_TIMELINE_ROWS)].map(p => p.id)), t('report.view.link.projects')))
        : '';
    if (!all.length) return section('timeline', `${head}<p class="rv-empty">${esc(t('report.view.timeline.empty'))}</p>${undatedNote}`);
    const rows = all.slice(0, MAX_TIMELINE_ROWS);

    const monthStart = iso => `${iso.slice(0, 7)}-01`;
    const addMonths = (iso, n) => { const d = new Date(dayMs(iso)); d.setUTCMonth(d.getUTCMonth() + n); return d.toISOString().slice(0, 10); };
    let t0iso = monthStart(rows[0].startDate < r.cutDate ? rows[0].startDate : r.cutDate);
    const lastEnd = rows.reduce((mx, p) => (p.endDate && p.endDate > mx ? p.endDate : mx), r.cutDate);
    let t1iso = addMonths(monthStart(lastEnd), 1);
    if (dayMs(t1iso) < dayMs(addMonths(t0iso, 3))) t1iso = addMonths(t0iso, 3);
    if (dayMs(t1iso) > dayMs(addMonths(t0iso, MAX_TIMELINE_MONTHS))) {
        // Too long to draw: keep the cut date in view (6 months of history),
        // bars outside the window are clipped and labelled with their real end.
        const anchor = addMonths(monthStart(r.cutDate), -6);
        if (anchor > t0iso) t0iso = anchor;
        t1iso = addMonths(t0iso, MAX_TIMELINE_MONTHS);
    }
    const t0 = dayMs(t0iso), t1 = dayMs(t1iso), cut = dayMs(r.cutDate);
    // The cut-date tag sits under the rows so it never covers the month labels.
    const W = 820, L = 200, R = 20, rowH = 30, top = 28;
    const rowsBottom = top + rows.length * rowH + 4;
    const H = rowsBottom + 24;
    const x = ms => L + ((Math.min(Math.max(ms, t0), t1) - t0) / (t1 - t0)) * (W - L - R);
    const monFmt = new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' });
    const monYearFmt = new Intl.DateTimeFormat(locale, { month: 'short', year: '2-digit', timeZone: 'UTC' });
    const totalMonths = Math.round((t1 - t0) / (30.44 * 86400000));
    const step = totalMonths > 14 ? 2 : 1;

    let s = `<svg class="rv-chart" viewBox="0 0 ${W} ${H}" role="img" aria-label="${esc(t('report.view.timeline.heading'))}">`;
    let i = 0;
    for (let d = new Date(t0); d.getTime() < t1; d.setUTCMonth(d.getUTCMonth() + 1), i++) {
        const xx = x(d.getTime());
        s += `<line class="rv-grid" x1="${xx}" x2="${xx}" y1="${top - 6}" y2="${rowsBottom}"/>`;
        if (i % step === 0) {
            const label = (i === 0 || d.getUTCMonth() === 0) ? monYearFmt.format(d) : monFmt.format(d);
            s += `<text x="${xx + 4}" y="${top - 12}">${esc(label)}</text>`;
        }
    }
    rows.forEach((p, idx) => {
        const yy = top + idx * rowH, cy = yy + rowH / 2, bh = 14, by = cy - bh / 2;
        const xa = x(dayMs(p.startDate));
        const endMs = p.endDate ? dayMs(p.endDate) : null;
        const xb = endMs ? x(endMs) : x(t1);
        const late = p.state === 'late';
        const pctLabel = p.total ? `${p.donePct}%` : t('report.summary.state.noTasks');
        let label;
        if (!endMs) label = `${t('report.view.timeline.noEnd')} · ${pctLabel}`;
        else if (late) label = t('report.view.timeline.late', { pct: p.donePct, days: p.daysOverdue });
        else if (endMs > t1) label = t('report.view.timeline.endsLater', { pct: pctLabel, date: monYearFmt.format(new Date(endMs)) });
        else label = pctLabel;
        const tip = `<b>${esc(esc(p.name))}</b><br>${esc(esc(`${f.day(p.startDate)} → ${p.endDate ? f.day(p.endDate) : t('report.view.timeline.noEnd')}`))}<br>${esc(esc(p.total ? t('report.view.timeline.tipDone', { pct: p.donePct, done: p.counts.done, total: p.total }) : t('report.summary.state.noTasks')))}`;

        s += `<text class="rv-lbl" x="0" y="${cy + 4}"><title>${esc(p.name)}</title>${esc(truncate(p.name, 30))}</text>`;
        s += `<g data-tip="${tip}">`;
        s += `<rect x="${L}" y="${yy}" width="${W - L - R}" height="${rowH}" class="rv-hit"/>`;
        s += `<rect x="${xa}" y="${by}" width="${Math.max(xb - xa, 4)}" height="${bh}" rx="4" class="rv-track${late ? ' late' : ''}${endMs ? '' : ' open'}"/>`;
        if (p.total && p.donePct > 0) s += `<rect x="${xa}" y="${by}" width="${Math.max((xb - xa) * p.donePct / 100, 2)}" height="${bh}" rx="4" class="rv-progress${late ? ' late' : ''}"/>`;
        const fitsRight = xb + 6 + textWidth(label, 11) <= W - 2;
        if (!endMs) s += `<text class="rv-bar-lbl" x="${xa + 8}" y="${cy + 4}">${esc(label)}</text>`;
        else if (fitsRight && endMs <= t1) s += `<text class="rv-bar-lbl${late ? ' late' : ''}" x="${xb + 6}" y="${cy + 4}">${esc(label)}</text>`;
        else s += `<text class="rv-bar-lbl${late ? ' late' : ''}" x="${xb - 6}" y="${cy + 4}" text-anchor="end">${esc(label)}</text>`;
        s += `</g>`;
    });
    if (cut >= t0 && cut <= t1) {
        const xc = x(cut);
        const cutLabel = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(cut));
        const bw = Math.max(52, textWidth(cutLabel, 10) + 14);
        const tx = Math.min(Math.max(xc, L + bw / 2), W - R - bw / 2);
        s += `<line class="rv-cut" x1="${xc}" x2="${xc}" y1="${top - 6}" y2="${rowsBottom + 4}"/>`;
        s += `<rect class="rv-cut-tag" x="${tx - bw / 2}" y="${rowsBottom + 4}" width="${bw}" height="16" rx="3"/><text class="rv-cut-text" x="${tx}" y="${rowsBottom + 15.5}" text-anchor="middle">${esc(cutLabel)}</text>`;
    }
    s += `</svg>`;
    return section('timeline', `${head}<div class="rv-scroll-x"><div class="rv-gantt">${s}</div></div>${undatedNote}`);
}

// ── Work pace (burn-up) ─────────────────────────────────────────────────
function throughputSection(r, t, f, locale) {
    const tp = r.throughput;
    if (!tp.hasHistory) return '';
    const months = activeMonths(r);
    const n = months.length;
    const monFmt = new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' });
    const monYearFmt = new Intl.DateTimeFormat(locale, { month: 'short', year: '2-digit', timeZone: 'UTC' });
    const labelFor = (key, i) => {
        const d = new Date(`${key}-01T00:00:00Z`);
        const base = (i === 0 || d.getUTCMonth() === 0) ? monYearFmt.format(d) : monFmt.format(d);
        return i === n - 1 ? `${base}*` : base;
    };
    const top = niceCeil(Math.max(...months.map(mm => mm.cumCreated), 1));
    // Right margin fits the "N unfinished" gap label inside the chart, so it never
    // spills toward the side column.
    const W = 520, H = 280, m = { l: 44, r: 96, t: 16, b: 32 };
    const x = i => m.l + (n === 1 ? 0 : i * (W - m.l - m.r) / (n - 1));
    const y = v => H - m.b - (v / top) * (H - m.t - m.b);
    let s = `<svg class="rv-chart" viewBox="0 0 ${W} ${H}" role="img" aria-label="${esc(t('report.summary.throughput.heading'))}">`;
    for (let k = 0; k <= 4; k++) {
        const v = (top / 4) * k;
        s += `<line class="rv-grid" x1="${m.l}" x2="${W - m.r}" y1="${y(v)}" y2="${y(v)}"/><text x="${m.l - 8}" y="${y(v) + 4}" text-anchor="end">${esc(f.num(v))}</text>`;
    }
    s += `<line class="rv-axis" x1="${m.l}" x2="${W - m.r}" y1="${y(0)}" y2="${y(0)}"/>`;
    months.forEach((mm, i) => { s += `<text x="${x(i)}" y="${H - 10}" text-anchor="middle">${esc(labelFor(mm.month, i))}</text>`; });
    const path = key => months.map((mm, i) => `${i ? 'L' : 'M'}${x(i)},${y(mm[key])}`).join(' ');
    s += `<path class="rv-area-done" d="${path('cumCompleted')} L${x(n - 1)},${y(0)} L${x(0)},${y(0)} Z"/>`;
    s += `<path class="rv-line-created" d="${path('cumCreated')}"/>`;
    s += `<path class="rv-line-done" d="${path('cumCompleted')}"/>`;
    const last = months[n - 1];
    const gap = last.cumCreated - last.cumCompleted;
    if (gap > 0) {
        const gx = x(n - 1) + 10;
        s += `<line class="rv-gap" x1="${gx}" x2="${gx}" y1="${y(last.cumCreated)}" y2="${y(last.cumCompleted)}"/>`;
        s += `<text class="rv-lbl" x="${gx + 6}" y="${(y(last.cumCreated) + y(last.cumCompleted)) / 2 + 4}">${esc(gap)}</text>`;
        s += `<text x="${gx + 6}" y="${(y(last.cumCreated) + y(last.cumCompleted)) / 2 + 18}">${esc(t('report.view.throughput.gapLabel'))}</text>`;
    }
    months.forEach((mm, i) => {
        const tip = `<b>${esc(esc(f.month(mm.month)))}</b><br>${esc(esc(t('report.view.throughput.tipCum', { created: mm.cumCreated, completed: mm.cumCompleted })))}<br>${esc(esc(t('report.view.throughput.tipMonth', { created: mm.created, completed: mm.completed })))}`;
        const half = n === 1 ? 20 : (W - m.l - m.r) / (n - 1) / 2;
        s += `<g data-tip="${tip}"><rect class="rv-hit" x="${x(i) - half}" y="${m.t}" width="${half * 2}" height="${H - m.t - m.b}"/>`;
        const r0 = i === n - 1 ? 4.5 : 3;
        s += `<circle class="rv-pt-created" cx="${x(i)}" cy="${y(mm.cumCreated)}" r="${r0}"/><circle class="rv-pt-done" cx="${x(i)}" cy="${y(mm.cumCompleted)}" r="${r0}"/></g>`;
    });
    s += `<text class="rv-lbl" x="${x(n - 1) - 6}" y="${y(last.cumCreated) - 10}" text-anchor="end">${esc(last.cumCreated)}</text>`;
    if (Math.abs(y(last.cumCreated) - y(last.cumCompleted)) > 16) {
        s += `<text class="rv-lbl" x="${x(n - 1) - 6}" y="${y(last.cumCompleted) - 10}" text-anchor="end">${esc(last.cumCompleted)}</text>`;
    }
    s += `</svg>`;
    const cutDay = Number(r.cutDate.slice(8, 10));
    s += `<p class="rv-sub">${esc(t('report.view.throughput.partial', { month: f.month(last.month), day: cutDay }))}</p>`;

    const avgCreated = months.slice(0, -1).reduce((sum, mm) => sum + mm.created, 0) / Math.max(1, n - 1);
    const peak = months.slice(0, -1).reduce((best, mm) => (mm.created > (best?.created || 0) ? mm : best), null);
    const peakNote = peak && peak.created >= Math.max(5, avgCreated * 1.5)
        ? t('report.view.throughput.peak', { month: f.month(peak.month), count: peak.created })
        : t('report.view.throughput.gap');
    const side = `
        <div><div class="rv-big">${esc(t('report.view.throughput.avgBig', { avg: f.num(tp.avgCompletedPerMonth) }))}</div>${esc(t('report.view.throughput.avgNote'))}</div>
        <div><div class="rv-big">${esc(t('report.view.throughput.remainingBig', { count: tp.remaining }))}</div>${esc(tp.monthsToClear ? t('report.view.throughput.remainingNote', { months: tp.monthsToClear }) : t('report.view.throughput.remainingNoPace'))}</div>
        <div class="rv-callout">${esc(peakNote)}</div>`;
    return section('throughput', `
        ${secHead(t('report.summary.throughput.heading'), t('report.view.throughput.sub'), legend([['created', t('report.summary.throughput.colCreated')], ['done', t('report.summary.throughput.colCompleted')]]))}
        <div class="rv-two"><div>${s}</div><div class="rv-side">${side}</div></div>`);
}

// ── Deadlines ───────────────────────────────────────────────────────────
function deadlinesSection(r, t, f, locale, links) {
    let aging;
    if (r.kpis.overdue === 0) {
        aging = `<p class="rv-empty">${esc(t('report.summary.deadlines.noOverdue'))}</p>`;
    } else {
        const max = Math.max(1, ...r.aging.map(b => b.count));
        // Each bucket's track opens exactly its overdue tasks in a new tab.
        aging = `<div class="rv-hbars">${r.aging.map((b, i) => {
            const label = t(`report.summary.deadlines.bucket.${b.key}`);
            const href = b.count ? links.tasks(b.taskIds || []) : '';
            const tip = `<b>${esc(esc(label))}</b><br>${esc(esc(t('report.view.deadlines.agingTip', { count: b.count })))}${href ? `<br><span class=&quot;rv-tip-hint&quot;>${esc(esc(t('report.view.portfolio.clickHint')))}</span>` : ''}`;
            const bar = b.count
                ? `<div class="rv-hbar rv-age-${i}" style="width:${((b.count / max) * 100).toFixed(2)}%"></div>`
                : `<span class="rv-muted">${esc(t('report.view.deadlines.none'))}</span>`;
            const track = href
                ? `<a class="rv-hbar-track" href="${esc(href)}" target="_blank" rel="noopener" data-tip="${tip}" aria-label="${esc(`${label}: ${b.count}`)}">${bar}</a>`
                : `<div class="rv-hbar-track" data-tip="${tip}">${bar}</div>`;
            return `
            <span>${esc(label)}</span>
            ${track}
            <span class="rv-num">${esc(b.count)}</span>`;
        }).join('')}</div>`;
    }

    const dayFmt = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' });
    const wdFmt = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });
    const rows = r.upcoming.rows.slice(0, MAX_UPCOMING);
    let upcoming;
    if (!rows.length) {
        upcoming = `<p class="rv-empty">${esc(t('report.summary.deadlines.upcomingEmpty'))}</p>`;
    } else {
        upcoming = `<ul class="rv-upcoming">${rows.map(u => {
            const d = new Date(`${u.date}T00:00:00Z`);
            let body;
            if (u.kind === 'projectStart') body = esc(t('report.summary.deadlines.projectStart', { name: u.name, count: u.count })).replace(esc(u.name), `<b>${nameLink(links.project(u.projectId), u.name)}</b>`);
            else if (u.kind === 'projectEnd') body = esc(t('report.summary.deadlines.projectEnd', { name: u.name, count: u.count })).replace(esc(u.name), `<b>${nameLink(links.project(u.projectId), u.name)}</b>`);
            else body = `<b>${nameLink(links.taskGroup(u.taskIds), u.name)}</b>${u.count > 1 ? ` ×${esc(u.count)}` : ''}${u.projectName ? ` · ${nameLink(links.project(u.projectId), u.projectName)}` : ''}`;
            return `<li><span class="d">${esc(dayFmt.format(d))}<small>${esc(wdFmt.format(d))}</small></span><span class="t">${body}</span></li>`;
        }).join('')}</ul>`;
        const hidden = r.upcoming.total - rows.length;
        if (hidden > 0) upcoming += `<p class="rv-sub">${esc(t('report.summary.deadlines.more', { count: hidden }))}${linkHtml(links.tasks(r.upcoming.hiddenTaskIds), t('report.view.link.tasks'))}</p>`;
    }
    return section('deadlines', `
        ${secHead(t('report.summary.deadlines.heading'))}
        <div class="rv-two">
            <div><p class="rv-h3">${esc(t('report.summary.deadlines.agingHeading'))}</p>${aging}</div>
            <div><p class="rv-h3">${esc(t('report.summary.deadlines.upcomingHeading', { days: r.upcoming.days }))}</p>${upcoming}</div>
        </div>`);
}

// ── Needs attention ─────────────────────────────────────────────────────
function attentionSection(r, t, f, links) {
    const head = secHead(t('report.summary.attention.heading'), t('report.summary.attention.intro'));
    if (!r.attention.rows.length) return section('attention', `${head}<p class="rv-empty">${esc(t('report.summary.attention.empty'))}</p>`);
    const none = esc(t('report.summary.common.none'));
    const rows = r.attention.rows.map(g => {
        const chips = STATUS_ORDER.filter(s => g.statusCounts[s] > 0)
            .map(s => `<span class="rv-chip"><i class="rv-fill-${s}"></i>${esc(t(`tasks.status.${s}`))}${g.count > 1 ? ` ${esc(g.statusCounts[s])}` : ''}</span>`).join('<br>');
        const priorities = REPORT_PRIORITIES.filter(priority => g.priorityCounts?.[priority] > 0)
            .map(priority => `<span class="rv-prio rv-prio-${priority}">${esc(t(`tasks.priority.${priority}`))}${g.priorityCounts[priority] > 1 ? ` ${esc(g.priorityCounts[priority])}` : ''}</span>`).join('');
        const lbl = key => `data-label="${esc(t(`report.summary.attention.${key}`))}"`;
        return `<tr>
            <td class="task">${nameLink(links.taskGroup(g.taskIds), g.title)}${g.count > 1 ? `<span class="rv-count">×${esc(g.count)}</span>` : ''}</td>
            <td ${lbl('colProject')}>${g.projectName ? nameLink(links.project(g.projectId), g.projectName) : esc(t('report.summary.projects.unassigned'))}</td>
            <td ${lbl('colStatus')}>${chips}</td>
            <td class="center" ${lbl('colPriority')}>${priorities ? `<span class="rv-priority-list">${priorities}</span>` : `<span class="rv-muted">${none}</span>`}</td>
            <td class="num late" ${lbl('colOverdue')}>${esc(t('report.summary.attention.days', { days: g.maxDaysOverdue }))}</td>
        </tr>`;
    }).join('');
    const more = r.attention.totalGroups > r.attention.rows.length
        ? `<p class="rv-sub">${esc(t('report.summary.attention.more', { shown: r.attention.rows.length, total: r.attention.totalGroups }))}${linkHtml(links.tasks(r.attention.hiddenTaskIds), t('report.view.link.tasks'))}</p>` : '';
    return section('attention', `${head}
        <div class="rv-scroll-x"><table class="rv-table">
            <thead><tr>
                <th>${esc(t('report.summary.attention.colTask'))}</th>
                <th>${esc(t('report.summary.attention.colProject'))}</th>
                <th>${esc(t('report.summary.attention.colStatus'))}</th>
                <th class="center">${esc(t('report.summary.attention.colPriority'))}</th>
                <th class="num">${esc(t('report.summary.attention.colOverdue'))}</th>
            </tr></thead>
            <tbody>${rows}</tbody>
        </table></div>${more}`);
}

// ── Footer ──────────────────────────────────────────────────────────────
function methodFooter(r, t, f, tolerance) {
    const q = r.dataQuality;
    const items = [
        ['projectsWithoutTasks', q.projectsWithoutTasks],
        ['projectsWithoutDates', q.projectsWithoutDates],
        ['tasksWithoutProject', q.tasksWithoutProject],
        ['tasksInArchivedProjects', q.tasksInArchivedProjects],
        ['doneWithoutCompletedDate', q.doneWithoutCompletedDate]
    ].filter(([, n]) => n > 0).map(([key, n]) => `${esc(t(`report.summary.quality.${key}`))}: ${esc(n)}`);
    if (!r.throughput.hasHistory) items.push(esc(t('report.summary.quality.throughputHidden')));
    return `<footer class="rv-method">
        <div><b>${esc(t('report.summary.method.heading'))}.</b> ${esc(t('report.summary.method.body', { date: f.day(r.cutDate), tolerance }))}</div>
        <div><b>${esc(t('report.summary.quality.heading'))}.</b> ${items.length ? items.join(' · ') : esc(t('report.summary.quality.none'))}</div>
    </footer>`;
}

/**
 * Render the report page (the white "sheet"), without the app toolbar.
 *
 * @param {Object} report - Output of computeSummaryReport
 * @param {Object} options
 * @param {Function} options.t - i18n lookup t(key, vars)
 * @param {string} [options.locale='en-US']
 * @param {string} [options.title] - Report title (defaults to the scope name)
 * @param {string} [options.baseUrl] - App URL for deep links (e.g. https://host/); '' keeps them relative
 * @param {Date} [options.generatedAt] - When the facts were computed (time shown next to the cut date)
 * @param {number} [options.tolerance=10] - Pace tolerance shown in the method note
 * @returns {string} HTML
 */
export function renderSummaryReportHtml(report, options = {}) {
    const t = typeof options.t === 'function' ? options.t : (key => key);
    const locale = options.locale || 'en-US';
    const f = makeFormatters(locale);
    const title = options.title || t('report.view.defaultTitle');
    const tolerance = Number.isFinite(options.tolerance) ? options.tolerance : 10;
    const links = makeReportLinks(options.baseUrl);
    const timeLabel = options.generatedAt instanceof Date && !Number.isNaN(options.generatedAt.getTime())
        ? new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(options.generatedAt)
        : '';
    return `<article class="rv-page">
        ${headerSection(report, t, f, title, timeLabel)}
        ${kpiSection(report, t, f)}
        ${highlightsSection(report, t, f)}
        ${portfolioSection(report, t, f, links)}
        ${paceSection(report, t, f, links)}
        ${timelineSection(report, t, f, locale, links)}
        ${throughputSection(report, t, f, locale)}
        ${deadlinesSection(report, t, f, locale, links)}
        ${attentionSection(report, t, f, links)}
        ${methodFooter(report, t, f, tolerance)}
    </article>`;
}
